import { z } from 'zod';
export const HEARTBEAT_MS = 15_000;
/** Two missed heartbeats. Past this the peer is not answering. */
export const STALE_AFTER_MS = 35_000;
/** Past this we stop calling it "asleep" and call it offline. */
export const OFFLINE_AFTER_MS = 5 * 60_000;
export const PROTOCOL_VERSION = 2;
/**
 * PTY output is raw bytes and routinely splits multi-byte UTF-8 sequences, so it
 * cannot ride inside a JSON string. Everything terminal-shaped travels base64.
 */
const B64 = z.string().max(4 * 1024 * 1024);
/**
 * The one ceiling for a file crossing this wire, in either direction.
 *
 * It is a single constant because it used to be three, and they disagreed: the agent
 * allowed 25MB, this schema allowed 4MB of base64 (≈3MB of file), and the broker's
 * WebSocket server allowed an 8MB frame. Each one failed differently and none of them
 * said so — over 3MB the frame was silently dropped and the request timed out, and over
 * 8MB the broker **closed the agent's socket** (1009), so a big file looked like the Mac
 * briefly falling off the network. A photo straight from an iPhone is often 4–6MB, which
 * is squarely inside the dead zone.
 *
 * Everything below is derived from this number, so they cannot drift apart again.
 */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
/** What that becomes as base64: 4 characters per 3 bytes, rounded up to the padding. */
export const MAX_FILE_B64 = Math.ceil(MAX_FILE_BYTES / 3) * 4;
/** Frame budget for the WebSocket servers: the payload plus room for its JSON envelope. */
export const MAX_WS_PAYLOAD = MAX_FILE_B64 + 64 * 1024;
/**
 * A file's bytes. Deliberately a different, much larger bound than `B64` — a terminal
 * chunk has no business being megabytes, and a document has no business being capped at
 * the size of one.
 */
const FileB64 = z.string().max(MAX_FILE_B64);
/** tmux session names: what we accept as a project identifier. */
export const ProjectName = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9._-]+$/, 'project name must be a bare directory name');
export const SessionId = z.string().min(1).max(64).regex(/^[A-Za-z0-9._-]+$/);
/**
 * The projects root itself, as a project you can open.
 *
 * A sentinel rather than the folder's real name: the name that would be natural
 * (`WebstormProjects`) is also a name a directory inside the root could have, and
 * then two different entries would resolve to two different paths under one
 * identifier. This one cannot collide — the root has no child called `__root__`
 * because `listProjects` never emits a child under that name. The agent sends the
 * folder's actual name alongside it as `label`, which is what people see.
 */
export const ROOT_PROJECT = '__root__';
/**
 * The console: a bare login shell in the home directory, as a project you can open.
 *
 * A sentinel for the same reason as ROOT_PROJECT, and one step further — this one has
 * no directory under the projects root at all, so it can never collide with a real
 * project name and never appears in `listProjects`. The agent maps it to `$HOME`.
 *
 * Nothing about the session itself is special: every session here has always been a
 * login shell in tmux, and opening a project merely types `codex` into it afterwards.
 * The console is that same session with nothing typed into it — which is why this is a
 * name and a cwd rather than a new kind of session.
 */
export const CONSOLE_PROJECT = '__console__';
// ─── entities ────────────────────────────────────────────────────────────────
export const Project = z.object({
    name: z.string(),
    /** What to show instead of `name` when the two differ — see ROOT_PROJECT. */
    label: z.string().optional(),
    path: z.string(),
    hasSession: z.boolean(),
    lastUsedAt: z.number().nullable(),
});
export const Session = z.object({
    id: z.string(),
    project: z.string(),
    cwd: z.string(),
    createdAt: z.number(),
    cols: z.number(),
    rows: z.number(),
    alive: z.boolean(),
});
/**
 * `stale` is a heuristic, not a fact. tcpkeepalive + powernap mean the Mac's socket
 * often outlives the Mac being awake, so "socket open" never implies "responding".
 * Only a fresh pong proves liveness.
 */
export const LinkState = z.enum(['online', 'stale', 'offline']);
/**
 * What the phone wants the Mac to be doing when nobody is typing.
 *
 *  - `awake`: stay up and reachable, even on battery with the lid shut. This is the
 *    "I'm working" state — the Mac never sleeps on its own until told otherwise.
 *  - `doze`: sleep deeply to spare the battery, waking itself on a timer just long
 *    enough to ask the broker whether that has changed. A sleeping Mac behind NAT
 *    cannot be reached from outside (verified: no Apple mechanism wakes a Wi-Fi
 *    laptop on battery from the internet), so "wake me" is a flag the Mac collects
 *    on its next scheduled poll rather than a packet we can push to it.
 *
 * The desire lives on the broker, which always outlives the Mac's sleep, and is
 * handed to the agent on every reconnect — including the brief ones during a poll.
 */
export const PowerMode = z.enum(['awake', 'doze']);
/**
 * A bundle identifier, validated rather than trusted.
 *
 * This string is interpolated into an AppleScript source line (`tell application id
 * "…"`), which is the one place in this project where client input reaches something
 * resembling an interpreter. The character class is what keeps a crafted id from
 * closing the quote and appending statements. It is deliberately narrower than what
 * Apple technically permits — every real bundle id fits.
 */
export const BundleId = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, 'bundle id must be a bare reverse-DNS identifier');
/**
 * Path to an `.app` bundle. Absolute, no traversal, no quotes — `open -a` takes it as
 * an argv element (never a shell string), so this is defence in depth rather than the
 * only guard.
 */
export const AppPath = z
    .string()
    .min(2)
    .max(1024)
    .regex(/^\/[^\0"'`$\n]*\.app$/, 'app path must be an absolute path to a .app bundle')
    .refine((p) => !p.includes('/../'), 'app path must not traverse');
/** A running app with a Dock icon. Background agents and XPC services are excluded —
 *  they are not things anyone means by "close that app". */
export const MacApp = z.object({
    name: z.string(),
    bundleId: z.string(),
    /** Every process the app owns. Chrome alone contributes several, and a force quit
     *  has to reach all of them rather than just the first one listed. */
    pids: z.array(z.number()),
});
/**
 * One window of a running app.
 *
 * Windows, not processes — that distinction is the whole point. Chrome runs ~41
 * processes for two windows: renderers, GPU helpers, a crash handler. A list of those
 * is twenty identical rows named "Google Chrome Helper (Renderer)" and tells you
 * nothing about which one holds the page you wanted to close.
 *
 * `index` is the app's own window ordering and is inherently unstable — windows
 * reorder as they are focused. It always travels with `title`, which the agent
 * re-checks against the live list before closing anything.
 */
export const AppWindow = z.object({ index: z.number().int().min(1).max(200), title: z.string() });
export const AppWindows = z.object({ bundleId: z.string(), windows: z.array(AppWindow) });
export const InstalledApp = z.object({ name: z.string(), path: z.string() });
export const AppList = z.object({
    running: z.array(MacApp),
    installed: z.array(InstalledApp),
});
/**
 * One live network attachment. Every field past `iface` is nullable on purpose:
 * macOS hands these out through half a dozen unrelated tools, any of which can be
 * missing, slow or permission-gated, and a metric we could not read must degrade to
 * "unknown" rather than sink the whole payload.
 */
export const NetLink = z.object({
    iface: z.string(),
    kind: z.enum(['wifi', 'ethernet', 'vpn', 'other']),
    /** Friendly name from networksetup — "Wi-Fi", "USB 10/100 LAN", "Thunderbolt Bridge". */
    service: z.string().nullable(),
    ip: z.string().nullable(),
    ssid: z.string().nullable(),
    signalDbm: z.number().nullable(),
    noiseDbm: z.number().nullable(),
    channel: z.string().nullable(),
    security: z.string().nullable(),
    /** The default route leaves through this one, so this is where traffic actually
     *  goes. With a VPN up that is a utun, not the Wi-Fi underneath it. */
    primary: z.boolean(),
});
/**
 * A snapshot of what the Mac is doing physically — the things you cannot tell from
 * a terminal. Collected on the Mac, cached on the broker, shown in the app.
 */
export const MacMetrics = z.object({
    at: z.number(),
    battery: z
        .object({
        percent: z.number().nullable(),
        source: z.enum(['ac', 'battery', 'ups', 'unknown']),
        /** pmset's own vocabulary: charging / charged / discharging / finishing charge. */
        state: z.string().nullable(),
        /** Minutes to empty, or to full while charging. pmset prints 0:00 whenever it
         *  is still working the estimate out, which we report as null rather than as
         *  a confident "0 minutes left". */
        minutesLeft: z.number().nullable(),
    })
        .nullable(),
    /**
     * Share of the panel's current nit ceiling, plus the raw nits it came from.
     *
     * Not the position of the macOS slider: that scale is perceptual and undocumented,
     * so this reads lower in the middle of the range and matches at both ends. The nits
     * are carried alongside precisely so the percentage never has to be taken on faith.
     */
    brightness: z
        .object({ percent: z.number(), nits: z.number(), maxNits: z.number() })
        .nullable(),
    /** 1 / 5 / 15-minute load. Reported raw, with `cores` alongside, because a load of
     *  6 means something different on 8 cores than on 2. */
    load: z.tuple([z.number(), z.number(), z.number()]),
    cores: z.number(),
    memory: z.object({
        totalBytes: z.number(),
        /** Apple's own system-wide free percentage (`memory_pressure -Q`). Deliberately
         *  not a hand-rolled "used" figure: macOS counts compressed and cached pages in
         *  ways that make any such number arguable, and swap below is the honest
         *  pressure signal anyway. */
        freePercent: z.number().nullable(),
        swapUsedBytes: z.number().nullable(),
        swapTotalBytes: z.number().nullable(),
    }),
    disk: z.object({ totalBytes: z.number(), freeBytes: z.number() }).nullable(),
    uptimeSec: z.number(),
    net: z.array(NetLink),
});
/** Subscription windows reported by Codex app-server; timestamps are epoch ms. */
export const CodexLimit = z.object({
    key: z.string(),
    title: z.string(),
    primary: z.boolean(),
    percent: z.number().finite(),
    windowMinutes: z.number().positive().nullable(),
    resetsAt: z.number().finite().nullable(),
});
/** Only display data crosses the wire. Codex owns all authentication. */
export const CodexUsage = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        at: z.number(),
        windows: z.array(CodexLimit),
        credits: z.object({
            hasCredits: z.boolean(),
            unlimited: z.boolean(),
            balance: z.string().nullable(),
        }).nullable(),
        plan: z.string().nullable(),
    }),
    z.object({ ok: z.literal(false), at: z.number(), error: z.string() }),
]);
/**
 * A JPEG frame of the Mac's screen. Downscaled and recompressed on the Mac before it
 * ships — a Retina desktop is multiple megabytes raw, which has no business crossing a
 * phone link. `w`/`h` are the delivered pixel dimensions, not the panel's real ones.
 */
export const ScreenShot = z.object({ b64: B64, w: z.number(), h: z.number() });
/**
 * A filesystem path from the phone. execFile takes it as an argv element, so there is
 * no shell to inject into — but the agent still resolves it and refuses anything that
 * escapes the home directory. No NUL (would truncate the C string), no `..` segment.
 */
export const FsPath = z
    .string()
    .min(1)
    .max(4096)
    .regex(/^[^\0]+$/, 'path must not contain NUL')
    .refine((p) => !p.split('/').includes('..'), 'path must not traverse');
export const FsEntry = z.object({
    name: z.string(),
    path: z.string(),
    kind: z.enum(['dir', 'file', 'other']),
    /** Bytes for a file, null for a directory or anything unstattable. */
    size: z.number().nullable(),
    mtime: z.number().nullable(),
});
/** One process row for the "what's eating the Mac" list. */
export const Proc = z.object({
    pid: z.number().int(),
    cpu: z.number(),
    mem: z.number(),
    name: z.string(),
});
/**
 * A pointer position as a fraction of the delivered screenshot, 0..1 on each axis.
 * Sent normalised rather than in pixels so the phone never has to know the Mac's real
 * resolution — the agent maps it onto the main display's logical size on arrival.
 */
export const Frac = z.number().min(0).max(1);
/** A key chord like "cmd+c" or "ctrl+alt+t". Parsed on the Mac, bounded here. */
export const KeyCombo = z.string().min(1).max(64).regex(/^[a-z0-9+]+$/i, 'combo is tokens joined by +');
/**
 * An address typed on the phone. A bare "beget.com" is allowed — the agent prefixes
 * https:// — but whatever it resolves to must be http(s): `file:`, `chrome:` and
 * `javascript:` stay out of a browser that holds real sessions.
 */
export const WebUrl = z.string().trim().min(1).max(2048);
/**
 * One action on the Mac's own headless browser.
 *
 * The browser exists because the Mac's screen cannot be relied on: with the lid shut or
 * the brightness at zero, `screencapture` returns a black frame (verified), so a login
 * done by tapping on screenshots of the desktop is impossible exactly when the Mac is
 * left alone. A headless Chrome renders off-screen and screenshots its own page.
 *
 * `login` carries the password. It is never logged on either hop and lives only in
 * memory for the length of the request; `fresh` clears the site's cookies and storage
 * first, which is how "it might already be signed in as someone else" gets handled.
 */
export const WebOp = z.discriminatedUnion('op', [
    z.object({
        op: z.literal('login'),
        url: WebUrl,
        login: z.string().max(512),
        password: z.string().min(1).max(512),
        fresh: z.boolean(),
    }),
    z.object({ op: z.literal('open'), url: WebUrl }),
    /** A one-time code for the field the last login stopped at. */
    z.object({ op: z.literal('code'), code: z.string().min(1).max(64) }),
    z.object({ op: z.literal('click'), x: Frac, y: Frac }),
    z.object({ op: z.literal('type'), text: z.string().min(1).max(4096) }),
    z.object({ op: z.literal('key'), key: z.enum(['Enter', 'Tab', 'Escape', 'Backspace']) }),
    z.object({ op: z.literal('scroll'), dir: z.enum(['up', 'down']) }),
    z.object({ op: z.literal('back') }),
    /** Sign out of the site that is open now: its cookies and storage are dropped. */
    z.object({ op: z.literal('logout') }),
    z.object({ op: z.literal('shot') }),
]);
/**
 * What the browser shows after an action, and what the agent made of it.
 *
 * `signed_in` — the password field went away after submit; `rejected` — it stayed, with
 * the page's own error text in `message` when one was found; `code` — the site wants a
 * one-time code; `no_form` — no login form was found, the screenshot is there to tap;
 * `page` — the plain result of a non-login action; `error` — the action itself failed.
 */
export const WebView = z.object({
    status: z.enum(['signed_in', 'rejected', 'code', 'no_form', 'page', 'error']),
    url: z.string(),
    title: z.string(),
    message: z.string().nullable(),
    shot: ScreenShot.nullable(),
});
// ─── agent → broker ──────────────────────────────────────────────────────────
export const AgentMsg = z.discriminatedUnion('t', [
    z.object({
        t: z.literal('a.hello'),
        secret: z.string(),
        protocol: z.number(),
        hostname: z.string(),
        agentVersion: z.string(),
    }),
    z.object({ t: z.literal('a.pong'), ts: z.number() }),
    z.object({ t: z.literal('a.projects'), rid: z.string(), projects: z.array(Project) }),
    z.object({ t: z.literal('a.sessions'), rid: z.string().nullable(), sessions: z.array(Session) }),
    z.object({ t: z.literal('a.data'), sid: z.string(), b64: B64 }),
    z.object({ t: z.literal('a.exit'), sid: z.string(), code: z.number().nullable() }),
    z.object({ t: z.literal('a.err'), rid: z.string().nullable(), msg: z.string() }),
    z.object({ t: z.literal('a.metrics'), rid: z.string(), metrics: MacMetrics }),
    z.object({ t: z.literal('a.codex_usage'), rid: z.string(), usage: CodexUsage }),
    z.object({ t: z.literal('a.apps'), rid: z.string(), apps: AppList }),
    z.object({ t: z.literal('a.app_windows'), rid: z.string(), windows: AppWindows }),
    z.object({ t: z.literal('a.screen'), rid: z.string(), b64: B64, w: z.number(), h: z.number() }),
    /** Generic success ack for a fire-and-confirm action (click, lock, kill, …). */
    z.object({ t: z.literal('a.ok'), rid: z.string() }),
    z.object({ t: z.literal('a.clip'), rid: z.string(), text: z.string() }),
    z.object({ t: z.literal('a.fs'), rid: z.string(), path: z.string(), entries: z.array(FsEntry) }),
    z.object({ t: z.literal('a.file'), rid: z.string(), name: z.string(), b64: FileB64 }),
    z.object({ t: z.literal('a.ps'), rid: z.string(), procs: z.array(Proc) }),
    z.object({ t: z.literal('a.webcam'), rid: z.string(), b64: B64, w: z.number(), h: z.number() }),
    z.object({ t: z.literal('a.web'), rid: z.string(), view: WebView }),
]);
// ─── broker → agent ──────────────────────────────────────────────────────────
export const ServerToAgentMsg = z.discriminatedUnion('t', [
    z.object({ t: z.literal('s.hello_ack'), heartbeatMs: z.number() }),
    z.object({ t: z.literal('s.ping'), ts: z.number() }),
    z.object({ t: z.literal('s.list_projects'), rid: z.string() }),
    z.object({ t: z.literal('s.list_sessions'), rid: z.string() }),
    z.object({
        t: z.literal('s.create'),
        rid: z.string(),
        project: ProjectName,
        cols: z.number(),
        rows: z.number(),
        /** Typed into the fresh shell once it exists. The agent owns the timing, so the
         *  client never has to guess when the prompt is ready. */
        launch: z.string().max(512).nullable(),
    }),
    z.object({ t: z.literal('s.kill'), sid: SessionId }),
    z.object({ t: z.literal('s.input'), sid: SessionId, b64: B64 }),
    z.object({ t: z.literal('s.resize'), sid: SessionId, cols: z.number(), rows: z.number() }),
    z.object({ t: z.literal('s.subscribe'), sid: SessionId }),
    z.object({ t: z.literal('s.unsubscribe'), sid: SessionId }),
    /** The current power desire. Sent on every agent reconnect, and again whenever the
     *  phone flips it — so a Mac that just woke to poll learns it should stay up. */
    z.object({ t: z.literal('s.power'), mode: PowerMode }),
    z.object({ t: z.literal('s.metrics'), rid: z.string() }),
    z.object({ t: z.literal('s.codex_usage'), rid: z.string() }),
    z.object({ t: z.literal('s.list_apps'), rid: z.string() }),
    z.object({ t: z.literal('s.app_open'), rid: z.string(), path: AppPath }),
    /** `force` is never inferred. A graceful quit that fails leaves the app up with its
     *  "save changes?" sheet, and the only thing allowed to escalate from there is the
     *  user tapping again — losing unsaved work must be a decision, not a timeout. */
    z.object({ t: z.literal('s.app_quit'), rid: z.string(), bundleId: BundleId, force: z.boolean() }),
    z.object({ t: z.literal('s.app_windows'), rid: z.string(), bundleId: BundleId }),
    z.object({ t: z.literal('s.screenshot'), rid: z.string() }),
    // clipboard
    z.object({ t: z.literal('s.clip_get'), rid: z.string() }),
    z.object({ t: z.literal('s.clip_set'), rid: z.string(), text: z.string().max(256 * 1024) }),
    // files
    z.object({ t: z.literal('s.fs_list'), rid: z.string(), path: FsPath }),
    z.object({ t: z.literal('s.fs_get'), rid: z.string(), path: FsPath }),
    z.object({ t: z.literal('s.fs_put'), rid: z.string(), path: FsPath, b64: FileB64 }),
    // gui control — x/y are fractions of the screenshot, mapped to points on the Mac
    z.object({ t: z.literal('s.click'), rid: z.string(), x: Frac, y: Frac, right: z.boolean() }),
    z.object({ t: z.literal('s.type'), rid: z.string(), text: z.string().max(4096) }),
    z.object({ t: z.literal('s.keys'), rid: z.string(), combo: KeyCombo }),
    // system control
    z.object({ t: z.literal('s.volume'), rid: z.string(), level: z.number().int().min(0).max(100) }),
    z.object({ t: z.literal('s.lock'), rid: z.string() }),
    z.object({ t: z.literal('s.restart'), rid: z.string() }),
    z.object({ t: z.literal('s.shutdown'), rid: z.string() }),
    z.object({ t: z.literal('s.ps'), rid: z.string() }),
    z.object({ t: z.literal('s.kill_pid'), rid: z.string(), pid: z.number().int().positive() }),
    // webcam
    z.object({ t: z.literal('s.webcam'), rid: z.string() }),
    // headless browser
    z.object({ t: z.literal('s.web'), rid: z.string(), op: WebOp }),
    z.object({
        t: z.literal('s.window_close'),
        rid: z.string(),
        bundleId: BundleId,
        index: z.number().int().min(1).max(200),
        /** Compared against the live window list on the Mac before anything is closed —
         *  never interpolated into AppleScript, only matched as a plain string. */
        title: z.string().max(1024),
    }),
]);
// ─── client → broker ─────────────────────────────────────────────────────────
// Untrusted input from a phone. Validate every field, bound every number.
export const ClientMsg = z.discriminatedUnion('t', [
    z.object({ t: z.literal('c.ping'), ts: z.number() }),
    z.object({ t: z.literal('c.list_projects') }),
    z.object({ t: z.literal('c.list_sessions') }),
    z.object({
        t: z.literal('c.create'),
        project: ProjectName,
        cols: z.number().int().min(20).max(500),
        rows: z.number().int().min(5).max(200),
        launch: z.string().max(512).nullable().optional(),
    }),
    z.object({
        t: z.literal('c.attach'),
        sid: SessionId,
        cols: z.number().int().min(20).max(500),
        rows: z.number().int().min(5).max(200),
    }),
    z.object({ t: z.literal('c.detach'), sid: SessionId }),
    z.object({ t: z.literal('c.input'), sid: SessionId, b64: z.string().max(64 * 1024) }),
    z.object({
        t: z.literal('c.resize'),
        sid: SessionId,
        cols: z.number().int().min(20).max(500),
        rows: z.number().int().min(5).max(200),
    }),
    z.object({ t: z.literal('c.kill'), sid: SessionId }),
    /** Ask the Mac to stay awake (work) or doze off (spare the battery). */
    z.object({ t: z.literal('c.power'), mode: PowerMode }),
    /**
     * Whether this tab has the Mac screen open. Metrics cost a handful of process
     * spawns on a battery-powered laptop, so the broker samples slowly to keep the
     * header summary current and switches to a fast cadence only while somebody is
     * actually looking at the numbers.
     */
    z.object({ t: z.literal('c.metrics'), watch: z.boolean() }),
    /** "Re-read the Codex limits now." Unlike metrics there is no watch flag: the
     *  broker polls these on one slow cadence for everyone, because the badge is in the
     *  header and is therefore always on screen. This is the pull-to-refresh path. */
    z.object({ t: z.literal('c.codex_usage') }),
    z.object({ t: z.literal('c.apps') }),
    z.object({ t: z.literal('c.screenshot') }),
    // clipboard
    z.object({ t: z.literal('c.clip_get') }),
    z.object({ t: z.literal('c.clip_set'), text: z.string().max(256 * 1024) }),
    // files
    z.object({ t: z.literal('c.fs_list'), path: FsPath }),
    z.object({ t: z.literal('c.fs_get'), path: FsPath }),
    z.object({ t: z.literal('c.fs_put'), path: FsPath, b64: FileB64 }),
    /**
     * "Send me this file in Telegram." The broker pulls it off the Mac and posts it to the
     * owner's chat with the bot.
     *
     * This exists because a Telegram WebView cannot save a file. Navigating one to a blob
     * URL turns it black (verified, see README), so the app could show a preview and
     * nothing more — no PDF, no zip, no keeping anything. The chat sidesteps the WebView
     * entirely: iOS renders the PDF with its own viewer, and "Сохранить в Файлы" is right
     * there. The bot token and the owner's id are already on the broker for alerts.
     */
    z.object({ t: z.literal('c.file_send'), path: FsPath }),
    // gui control
    z.object({ t: z.literal('c.click'), x: Frac, y: Frac, right: z.boolean().optional() }),
    z.object({ t: z.literal('c.type'), text: z.string().max(4096) }),
    z.object({ t: z.literal('c.keys'), combo: KeyCombo }),
    // system control
    z.object({ t: z.literal('c.volume'), level: z.number().int().min(0).max(100) }),
    z.object({ t: z.literal('c.lock') }),
    z.object({ t: z.literal('c.restart') }),
    z.object({ t: z.literal('c.shutdown') }),
    z.object({ t: z.literal('c.ps') }),
    z.object({ t: z.literal('c.kill_pid'), pid: z.number().int().positive() }),
    // webcam
    z.object({ t: z.literal('c.webcam') }),
    // headless browser
    z.object({ t: z.literal('c.web'), op: WebOp }),
    z.object({ t: z.literal('c.app_open'), path: AppPath }),
    z.object({ t: z.literal('c.app_quit'), bundleId: BundleId, force: z.boolean() }),
    z.object({ t: z.literal('c.app_windows'), bundleId: BundleId }),
    z.object({
        t: z.literal('c.window_close'),
        bundleId: BundleId,
        index: z.number().int().min(1).max(200),
        title: z.string().max(1024),
    }),
]);
// ─── broker → client ─────────────────────────────────────────────────────────
export const ServerToClientMsg = z.discriminatedUnion('t', [
    z.object({ t: z.literal('s.pong'), ts: z.number() }),
    z.object({
        t: z.literal('s.link'),
        state: LinkState,
        latencyMs: z.number().nullable(),
        lastSeenAt: z.number().nullable(),
    }),
    z.object({ t: z.literal('s.projects'), projects: z.array(Project) }),
    z.object({ t: z.literal('s.sessions'), sessions: z.array(Session) }),
    /** Full serialized terminal state, sent on attach before any live data. */
    z.object({ t: z.literal('s.snapshot'), sid: z.string(), b64: B64 }),
    /** Output is flowing (or has just stopped) in a session — drives the "работает…"
     *  indicator without parsing Codex's TUI, which changes between releases. */
    z.object({ t: z.literal('s.busy'), sid: z.string(), busy: z.boolean() }),
    z.object({ t: z.literal('s.data'), sid: z.string(), b64: B64 }),
    z.object({ t: z.literal('s.exit'), sid: z.string(), code: z.number().nullable() }),
    z.object({ t: z.literal('s.err'), msg: z.string() }),
    /** Current power desire, so every open tab shows the same toggle state. */
    z.object({ t: z.literal('s.power'), mode: PowerMode }),
    /** Latest reading from the Mac, or null while it is asleep or has not answered
     *  yet — the app must be able to say "неизвестно" rather than show stale numbers
     *  as if they were current. */
    z.object({ t: z.literal('s.metrics'), metrics: MacMetrics.nullable() }),
    /** Codex's limits as last read, or null while the Mac is unreachable — same rule
     *  as metrics: a percentage from an hour ago must never pose as the current one. */
    z.object({ t: z.literal('s.codex_usage'), usage: CodexUsage.nullable() }),
    z.object({ t: z.literal('s.apps'), apps: AppList }),
    z.object({ t: z.literal('s.app_windows'), windows: AppWindows }),
    /** A screen frame the phone asked for. `at` is when the Mac captured it, so the app
     *  can show "2 сек назад" rather than implying it is live. */
    z.object({ t: z.literal('s.screen'), b64: B64, w: z.number(), h: z.number(), at: z.number() }),
    z.object({ t: z.literal('s.clip'), text: z.string() }),
    z.object({ t: z.literal('s.fs'), path: z.string(), entries: z.array(FsEntry) }),
    z.object({ t: z.literal('s.file'), name: z.string(), b64: FileB64 }),
    /** The file is in the chat. Named, because "отправлено" about an unnamed file is not
     *  something you can check against what actually arrived. */
    z.object({ t: z.literal('s.file_sent'), name: z.string() }),
    z.object({ t: z.literal('s.ps'), procs: z.array(Proc) }),
    z.object({ t: z.literal('s.webcam'), b64: B64, w: z.number(), h: z.number(), at: z.number() }),
    z.object({ t: z.literal('s.web'), view: WebView, at: z.number() }),
]);
// ─── helpers ─────────────────────────────────────────────────────────────────
export function encode(msg) {
    return JSON.stringify(msg);
}
/** Parse without throwing — a malformed frame must never take the process down. */
export function safeParse(schema, raw) {
    let json;
    try {
        json = JSON.parse(raw);
    }
    catch {
        return { ok: false, err: 'malformed json' };
    }
    const parsed = schema.safeParse(json);
    return parsed.success
        ? { ok: true, msg: parsed.data }
        : { ok: false, err: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
}
/** Full-jitter exponential backoff (AWS's formulation). Prevents thundering reconnects. */
export function backoffMs(attempt, capMs = 30_000, baseMs = 1_000) {
    const exp = Math.min(capMs, baseMs * 2 ** attempt);
    return Math.floor(exp * (0.5 + Math.random() * 0.5));
}
//# sourceMappingURL=index.js.map